import { toaster } from '@/shared/ui/toaster.component';
import { formatDateTimeShortToBrazil } from '@/shared/utils';
import { useNavigate } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  toNotificationViewModel,
  type NotificationViewModel,
} from '../config/notification-presenters';
import {
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useNotifications,
  useNotificationsScope,
  useUnreadNotificationsCount,
} from '../hooks';
import { NotificationPanel } from './notification-panel.component';

/** Quantas notificações o painel mostra antes do "Ver todas". */
const PANEL_PAGE_SIZE = 10;
/** Acima disso o badge vira "99+" — o back devolve `hasMore` no mesmo sentido. */
const BADGE_MAX = 99;

/**
 * Sino de notificações do header.
 *
 * Quem vê, e de qual corretora, é decidido por `useNotificationsScope` — a
 * mesma regra que a tela usa. Para a seguradora o sino segue a corretora
 * selecionada no header e some quando não há nenhuma.
 *
 * A mecânica do dropdown (abrir com animação, fechar no clique fora, ancoragem
 * pelo `--app-header-bottom` no mobile) é a mesma do `BrokerContextSelector` de
 * propósito: header lotado em telas pequenas é problema já resolvido lá.
 */
export const NotificationBell = memo(function NotificationBell() {
  const { enabled, partyCode, canMarkAsRead } = useNotificationsScope();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: unread } = useUnreadNotificationsCount(enabled, partyCode);
  // A lista só é buscada com o painel aberto: o que roda de minuto em minuto é
  // apenas a contagem.
  const notificationsQuery = useNotifications(
    { status: 'all', partyCode },
    { enabled: enabled && isOpen, limit: PANEL_PAGE_SIZE }
  );
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const notifications = useMemo<NotificationViewModel[]>(() => {
    const items =
      notificationsQuery.data?.pages.flatMap(page => page.items) ?? [];
    return items
      .slice(0, PANEL_PAGE_SIZE)
      .map(item => toNotificationViewModel(item, formatDateTimeShortToBrazil));
  }, [notificationsQuery.data]);

  const unreadCount = unread?.count ?? 0;
  const badgeLabel = useMemo(() => {
    if (unreadCount <= 0) return null;
    if (unread?.hasMore || unreadCount > BADGE_MAX) return `${BADGE_MAX}+`;
    return String(unreadCount);
  }, [unread?.hasMore, unreadCount]);

  const openMenu = useCallback(() => {
    setIsOpen(true);
    setTimeout(() => setIsAnimating(true), 10);
  }, []);

  const closeMenu = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => setIsOpen(false), 200);
  }, []);

  const toggleMenu = useCallback(() => {
    if (isOpen) closeMenu();
    else openMenu();
  }, [closeMenu, isOpen, openMenu]);

  const handleSelect = useCallback(
    (notification: NotificationViewModel) => {
      closeMenu();
      // A marcação não bloqueia a navegação: o destino é o que o usuário pediu,
      // e o back é idempotente se o clique for repetido. A seguradora só
      // acompanha — marcar daqui apagaria o badge do corretor.
      if (canMarkAsRead && !notification.read) {
        // O erro PRECISA aparecer: sem isto, a falha é um clique que navega e
        // deixa o badge intacto, sem nada que explique por quê.
        markAsRead.mutate(notification.id, {
          onError: error => {
            toaster.create({
              type: 'error',
              title: 'Não foi possível marcar como lida',
              description:
                error instanceof Error
                  ? error.message
                  : 'Tente novamente em instantes.',
            });
          },
        });
      }
      if (notification.target) {
        void navigate(notification.target);
      }
    },
    [canMarkAsRead, closeMenu, markAsRead, navigate]
  );

  const handleSeeAll = useCallback(() => {
    closeMenu();
    void navigate({ to: '/notifications' });
  }, [closeMenu, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    if (isOpen) document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeMenu]);

  if (!enabled) return null;

  return (
    <div ref={wrapperRef} className='relative'>
      {/* Botão cru, como o hamburguer deste mesmo header: o `Button` do design
          system nunca é usado só com ícone em lugar nenhum do portal, e o
          padding dele empurrava o sino para fora do alinhamento. */}
      <button
        type='button'
        onClick={toggleMenu}
        className='relative flex h-8 w-8 items-center justify-center rounded-md text-white transition-colors hover:bg-alba-blue-500'
        aria-haspopup='menu'
        aria-expanded={isOpen}
        aria-label={
          unreadCount > 0
            ? `Notificações: ${badgeLabel} não lidas`
            : 'Notificações'
        }
      >
        <Bell className='h-4.5 w-4.5' strokeWidth={1.8} />
        {badgeLabel && (
          <span className='absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-alba-red-500 px-1 text-[10px] font-semibold leading-none text-white'>
            {badgeLabel}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className={`fixed left-1/2 top-[var(--app-header-bottom,64px)] z-70 w-[calc(100vw-1.5rem)] max-w-88 -translate-x-1/2 overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-md shadow-gray-50 transition-all duration-200 ease-out sm:absolute sm:left-auto sm:right-0 sm:top-[3.2rem] sm:translate-x-0 ${
            isAnimating
              ? 'translate-y-0 opacity-100'
              : '-translate-y-2 opacity-0'
          }`}
        >
          <NotificationPanel
            notifications={notifications}
            isLoading={notificationsQuery.isLoading}
            errorMessage={notificationsQuery.error?.message}
            canMarkAsRead={canMarkAsRead}
            hasUnread={canMarkAsRead && unreadCount > 0}
            isMarkingAll={markAllAsRead.isPending}
            onSelect={handleSelect}
            onMarkAllAsRead={() => markAllAsRead.mutate()}
            onSeeAll={handleSeeAll}
          />
        </div>
      )}
    </div>
  );
});
